import React, { useMemo, useState, useCallback, Fragment } from "react";
import { useNavigate } from "react-router-dom";

interface User {
  _id: string;
  name: string;
  email: string;
  avatarUrl?: string;
}

interface Order {
  _id: string;
  dj: string | { _id: string; name: string; avatarUrl?: string };
  searchQuery: string;
  amount: number;
  createdAt: string;
  status?: "pending" | "queued" | "accepted" | "declined" | "played";
  currency?: string;
  trackImage?: string;
  trackArtists?: string[];
  trackAlbum?: string;
  totalAmount?: number;
  feeAmount?: number;
  paid?: boolean;
  customerEmail?: string;
  customerName?: string | null;
  clientNickname?: string | null;
  clientInstagram?: string | null;
  cardLast4?: string | null;
  originalAmount?: number | null;
  counterAmount?: number | null;
  isPriority?: boolean;
  tier?: string;
  declineReason?: string | null;
  isDisputed?: boolean;
}

interface Props {
  orders: Order[];
  users: User[];
}

function fmtImg(url?: string): string | null {
  if (!url) return null;
  return url.startsWith("http") ? url : `https://api.requesta.app${url}`;
}

export default function AdminOrdersTab({ orders, users }: Props) {
  const navigate = useNavigate();
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterDj, setFilterDj] = useState("all");
  const [sortKey, setSortKey] = useState<"date" | "amount">("date");
  const [searchQuery, setSearchQuery] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);

  // Stable user lookup
  const userMap = useMemo(() => {
    const m = new Map<string, User>();
    for (const u of users) m.set(u._id, u);
    return m;
  }, [users]);

  const resolveDj = useCallback(
    (djData: Order["dj"]) => {
      if (typeof djData === "object" && djData !== null) {
        return { name: djData.name, avatar: djData.avatarUrl, id: djData._id };
      }
      const found = userMap.get(djData);
      return { name: found?.name ?? "Unknown", avatar: found?.avatarUrl, id: djData as string };
    },
    [userMap]
  );

  const filtered = useMemo(() => {
    let res = orders.slice();
    if (filterStatus !== "all") res = res.filter((o) => o.status === filterStatus);
    if (filterDj !== "all") {
      res = res.filter((o) => {
        const id = typeof o.dj === "string" ? o.dj : o.dj._id;
        return id === filterDj;
      });
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      res = res.filter((o) =>
        (o.customerEmail || "").toLowerCase().includes(q) ||
        (o.customerName || "").toLowerCase().includes(q) ||
        (o.clientNickname || "").toLowerCase().includes(q) ||
        (o.clientInstagram || "").toLowerCase().includes(q) ||
        (o.searchQuery || "").toLowerCase().includes(q)
      );
    }
    if (dateFrom) {
      const from = new Date(dateFrom);
      from.setHours(0, 0, 0, 0);
      res = res.filter((o) => new Date(o.createdAt) >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      res = res.filter((o) => new Date(o.createdAt) <= to);
    }
    res.sort((a, b) =>
      sortKey === "amount" ? b.amount - a.amount : new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    return res;
  }, [orders, filterStatus, filterDj, sortKey, searchQuery, dateFrom, dateTo]);

  const stats = useMemo(() => {
    const paid = filtered.filter((o) => o.paid);
    const totalRev = paid.reduce((acc, o) => {
      if (typeof o.totalAmount === "number" && o.totalAmount > 0) return acc + o.totalAmount;
      return acc + o.amount / 0.85;
    }, 0);
    const platformFees = paid.reduce((acc, o) => {
      if (typeof o.feeAmount === "number" && o.feeAmount > 0) return acc + o.feeAmount;
      if (typeof o.totalAmount === "number" && o.totalAmount > o.amount) return acc + (o.totalAmount - o.amount);
      return acc + (o.amount * 0.15) / 0.85;
    }, 0);
    const count = filtered.length;
    const completed = filtered.filter((o) => o.status === "played" || o.status === "accepted").length;
    return {
      totalRev,
      platformFees,
      count,
      avg: paid.length > 0 ? totalRev / paid.length : 0,
      completionPct: count > 0 ? ((completed / count) * 100).toFixed(0) : "0",
    };
  }, [filtered]);

  return (
    <div className="adm-tab-container fade-in">
      {/* Stats row */}
      <div className="adm-grid-5" style={{ marginBottom: 14 }}>
        {[
          { label: "Total Revenue", value: `$${stats.totalRev.toFixed(2)}`, color: "" },
          { label: "Platform Fees (15%)", value: `$${stats.platformFees.toFixed(2)}`, color: "var(--adm-green)" },
          { label: "Requests", value: String(stats.count), color: "" },
          { label: "Avg. Request Value", value: `$${stats.avg.toFixed(2)}`, color: "" },
          { label: "Completion Rate", value: `${stats.completionPct}%`, color: "" },
        ].map((s) => (
          <div key={s.label} className="kpi-card kpi-card-compact">
            <div>
              <div className="kpi-label">{s.label}</div>
              <div className="kpi-value" style={s.color ? { color: s.color } : {}}>
                {s.value}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="adm-toolbar" style={{ flexWrap: "wrap" }}>
        <div className="adm-toolbar-group">
          <input
            className="adm-search"
            placeholder="Search customer, song..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: 200 }}
          />
          <select className="adm-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
            <option value="all">All Statuses</option>
            <option value="pending">Pending</option>
            <option value="queued">Queued</option>
            <option value="accepted">Accepted</option>
            <option value="played">Played</option>
            <option value="declined">Declined</option>
          </select>
          <select className="adm-select" value={filterDj} onChange={(e) => setFilterDj(e.target.value)}>
            <option value="all">All Talent</option>
            {users.map((u) => (
              <option key={u._id} value={u._id}>
                {u.name}
              </option>
            ))}
          </select>
        </div>
        <div className="adm-toolbar-group">
          <span className="muted-text" style={{ fontSize: 12 }}>From:</span>
          <input type="date" className="adm-search" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} style={{ width: 130 }} />
          <span className="muted-text" style={{ fontSize: 12 }}>To:</span>
          <input type="date" className="adm-search" value={dateTo} onChange={(e) => setDateTo(e.target.value)} style={{ width: 130 }} />
          <span className="muted-text" style={{ fontSize: 12, marginLeft: 8 }}>Sort:</span>
          {(["date", "amount"] as const).map((k) => (
            <button key={k} className={`filter-chip ${sortKey === k ? "active" : ""}`} onClick={() => setSortKey(k)}>
              {k.charAt(0).toUpperCase() + k.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {/* Table — ultra compact */}
      <div className="adm-scrollable-full">
        <table className="adm-table-light adm-table-xs sticky-header" style={{ tableLayout: "fixed", width: "100%" }}>
          <colgroup>
            <col style={{ width: "13%" }} />
            <col style={{ width: "30%" }} />
            <col style={{ width: "14%" }} />
            <col style={{ width: "10%" }} />
            <col style={{ width: "9%" }} />
            <col style={{ width: "13%" }} />
            <col style={{ width: "7%" }} />
            <col style={{ width: "4%" }} />
          </colgroup>
          <thead>
            <tr>
              <th>Talent</th>
              <th>Track</th>
              <th>Client</th>
              <th>Amount</th>
              <th>Status</th>
              <th>Date</th>
              <th>Paid</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((o) => {
              const dj = resolveDj(o.dj);
              const trackImg = fmtImg(o.trackImage);
              const artist = o.trackArtists?.join(", ") || "—";
              const isExpanded = expandedOrderId === o._id;

              return (
                <Fragment key={o._id}>
                <tr className="hover-row">
                  {/* DJ — clickable */}
                  <td>
                    <div className="dj-cell dj-cell-xs clickable" onClick={() => navigate(`/admin/dj/${dj.id}`)}>
                      {fmtImg(dj.avatar) ? (
                        <img src={fmtImg(dj.avatar)!} className="dj-avatar-xs" alt="" />
                      ) : (
                        <div className="dj-placeholder-xs">{dj.name[0].toUpperCase()}</div>
                      )}
                      <span className="dj-link-text truncate-inline">{dj.name}</span>
                    </div>
                  </td>

                  {/* Track */}
                  <td>
                    <div className="dj-cell dj-cell-xs">
                      {trackImg ? (
                        <img src={trackImg} className="track-img-xs" alt="" />
                      ) : (
                        <div className="track-placeholder-xs">♪</div>
                      )}
                      <span className="truncate-inline" style={{ fontSize: "0.76rem" }}>
                        {o.searchQuery}
                      </span>
                    </div>
                  </td>

                  {/* Client */}
                  <td
                    className="truncate-inline"
                    style={{ fontSize: "0.74rem", display: "table-cell", cursor: "pointer" }}
                    onClick={() => setExpandedOrderId(expandedOrderId === o._id ? null : o._id)}
                  >
                    {o.customerName || (o.clientInstagram ? `@${o.clientInstagram}` : null) || o.customerEmail || o.clientNickname || (o.cardLast4 ? `•••• ${o.cardLast4}` : null) || "—"}
                    <span className="muted-text" style={{ marginLeft: 4, fontSize: "0.65rem" }}>
                      {expandedOrderId === o._id ? "▲" : "▼"}
                    </span>
                  </td>

                  {/* Amount */}
                  <td style={{ fontWeight: 700, fontSize: "0.78rem", whiteSpace: "nowrap" }}>
                    {(() => {
                      const cur = (o.currency || "usd").toUpperCase();
                      // Counter pending: DJ declined with counter, fan hasn't responded
                      if (o.counterAmount && o.declineReason === "more_money") {
                        const diff = o.counterAmount - o.amount;
                        return (
                          <>
                            {cur} {o.counterAmount.toFixed(2)}
                            <span className="amount-addon amount-counter" title={`Countered — Original: ${cur} ${o.amount.toFixed(2)}`}>
                              +{diff.toFixed(2)}
                            </span>
                          </>
                        );
                      }
                      // Counter accepted or boost: originalAmount preserved
                      if (o.originalAmount != null && o.originalAmount !== o.amount) {
                        const diff = o.amount - o.originalAmount;
                        const isBoosted = !!o.isPriority;
                        return (
                          <>
                            {cur} {o.amount.toFixed(2)}
                            <span
                              className={`amount-addon ${isBoosted ? "amount-boost" : "amount-counter"}`}
                              title={`${isBoosted ? "Boosted" : "Countered"} — Original: ${cur} ${o.originalAmount.toFixed(2)}`}
                            >
                              +{diff.toFixed(2)}
                            </span>
                          </>
                        );
                      }
                      // Fast pass: bid was 2x DJ minimum, show the split
                      if (o.tier === "fast") {
                        const half = o.amount / 2;
                        return (
                          <>
                            {cur} {o.amount.toFixed(2)}
                            <span className="amount-addon amount-fast" title={`FastPass — base ${cur} ${half.toFixed(2)} + ${cur} ${half.toFixed(2)} premium`}>+{half.toFixed(2)}</span>
                          </>
                        );
                      }
                      return <>{cur} {o.amount.toFixed(2)}</>;
                    })()}
                  </td>

                  {/* Status */}
                  <td>
                    {o.status === "declined" && o.declineReason ? (
                      <span
                        className={`badge badge-xs badge-declined`}
                        title={
                          o.declineReason === "pas_ambience" ? "Not the Vibe"
                            : o.declineReason === "no_access" ? "No Access to Song"
                            : o.declineReason === "more_money" ? "Counter Offered"
                            : o.declineReason
                        }
                        style={{ cursor: "help" }}
                      >
                        declined *
                      </span>
                    ) : (
                      <span className={`badge badge-xs badge-${o.status || "pending"}`}>{o.status || "—"}</span>
                    )}
                    {o.isDisputed && (
                      <span className="badge badge-xs badge-declined" style={{ marginLeft: 4 }} title="This order has been disputed">
                        Disputed
                      </span>
                    )}
                  </td>

                  {/* Date */}
                  <td style={{ fontSize: "0.72rem", color: "var(--adm-text-sec)", whiteSpace: "nowrap" }}>
                    {new Date(o.createdAt).toLocaleDateString()}{" "}
                    {new Date(o.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                  </td>

                  {/* Paid */}
                  <td>
                    <span className={`badge badge-xs ${o.paid ? "badge-accepted" : "badge-declined"}`}>
                      {o.paid ? "✓" : "✗"}
                    </span>
                  </td>

                  {/* Link */}
                  <td>
                    <button
                      className="adm-btn-icon"
                      onClick={() => navigate(`/admin/dj/${dj.id}`)}
                      title={`View ${dj.name}`}
                    >
                      →
                    </button>
                  </td>
                </tr>
                {isExpanded && (
                  <tr>
                    <td colSpan={8} style={{ padding: 0, background: "#f9fafb" }}>
                      <div style={{ display: "flex", gap: 24, padding: "10px 14px", fontSize: "0.78rem" }}>
                        {[
                          { label: "Real Name", value: o.customerName },
                          { label: "Instagram", value: o.clientInstagram ? `@${o.clientInstagram}` : null },
                          { label: "Email", value: o.customerEmail },
                          { label: "Nickname", value: o.clientNickname },
                          { label: "Card", value: o.cardLast4 ? `•••• ${o.cardLast4}` : null },
                        ].map((item) => (
                          <div key={item.label}>
                            <div className="muted-text" style={{ fontSize: "0.65rem", textTransform: "uppercase", letterSpacing: "0.04em", fontWeight: 600, marginBottom: 2 }}>{item.label}</div>
                            <div style={{ fontWeight: 600 }}>{item.value || "—"}</div>
                          </div>
                        ))}
                      </div>
                    </td>
                  </tr>
                )}
                </Fragment>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={8} style={{ textAlign: "center", padding: 24, color: "#9ca3af" }}>
                  No orders match the current filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
